import { MenuItem, Select } from "@mui/material";
import { Button } from "components/atoms/Button";
import { Delete } from "components/atoms/Icon";
import { Header } from "components/atoms/Typography";
import MUITheme from "components/MUITheme";
import { useFeatureFlagEnabled } from "posthog-js/react";

const END_USES = [
    { value: "skin_heating", label: "Skin heating" },
    { value: "outdoor_air_heating", label: "Outdoor air heating" },
    { value: "skin_cooling", label: "Skin cooling" },
    { value: "outdoor_air_cooling", label: "Outdoor air cooling" },
    { value: "domestic_hot_water", label: "Domestic hot water" },
    { value: "lighting", label: "Lighting" },
    { value: "plug_load", label: "Plug load" },
    { value: "refrigeration", label: "Refrigeration" },
    { value: "fans", label: "Fans" },
];
const FUELS = [
    { value: "electricity", label: "Electricity" },
    { value: "natural_gas", label: "Natural gas" },
    { value: "steam", label: "Steam" },
];

function ConsumptionReductionEffectInput({
    consumptionReductionInputs,
    setConsumptionReductionInputs,
}: {
    consumptionReductionInputs: ConsumptionReductionEffect[];
    setConsumptionReductionInputs: (inputs: ConsumptionReductionEffect[]) => void;
}) {
    const featureEnabled = useFeatureFlagEnabled("consumption-reduction-measure-effect");
    const addConsumptionReduction = () => {
        setConsumptionReductionInputs([
            ...consumptionReductionInputs,
            {
                uid: crypto.randomUUID(),
                // Arbitrary ID is provided to object as it has not been saved to
                // a databse yet.
                id: 0,
                endUse: "",
                fuel: "",
                percentReduction: null,
            },
        ]);
    };
    const handleChange = (id: string, field: string, value: string | number | null) => {
        const newValues = consumptionReductionInputs.map((inputObj) =>
            inputObj.uid === id ? { ...inputObj, [field]: value } : inputObj
        );
        setConsumptionReductionInputs(newValues);
    };
    const removeConsumptionReduction = (uid: string) => {
        setConsumptionReductionInputs(consumptionReductionInputs.filter((input) => input.uid !== uid));
    };
    if (!featureEnabled) {
        return null;
    }
    if (consumptionReductionInputs.length === 0) {
        return (
            <div className="filter-container">
                <Button type="primary" onClick={addConsumptionReduction}>
                    + Add consumption reduction
                </Button>
            </div>
        );
    }
    return (
        <div>
            {consumptionReductionInputs.map((inputObj) => (
                <div key={inputObj.uid} className="effect-container">
                    <div className="effect-header">
                        <Header size="medium">Consumption reduction</Header>
                        <Button onClick={() => removeConsumptionReduction(inputObj.uid)} icon={<Delete />} />
                    </div>
                    <Header size="small">End use</Header>
                    <MUITheme primaryColor="#EB03AD">
                        <Select
                            required
                            value={inputObj.endUse}
                            onChange={(e) => handleChange(inputObj.uid, "endUse", e.target.value || "")}
                            style={{
                                width: "100%",
                                height: "35px",
                                minWidth: 0,
                                backgroundColor: "white",
                            }}
                        >
                            {END_USES.map((unit) => (
                                <MenuItem key={unit.value} value={unit.value}>
                                    {unit.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </MUITheme>
                    <Header size="small">Fuel</Header>
                    <MUITheme primaryColor="#EB03AD">
                        <Select
                            required
                            value={inputObj.fuel}
                            onChange={(e) => handleChange(inputObj.uid, "fuel", e.target.value || "")}
                            style={{
                                width: "100%",
                                height: "35px",
                                minWidth: 0,
                                backgroundColor: "white",
                            }}
                        >
                            {FUELS.map((unit) => (
                                <MenuItem key={unit.value} value={unit.value}>
                                    {unit.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </MUITheme>
                    <Header size="small">Reduction percentage</Header>
                    <input
                        key="consumptionReductionPercentage"
                        required={false}
                        type="number"
                        value={inputObj.percentReduction || ""}
                        className="filter-input"
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                            const value = e.target.value === null ? null : Number(e.target.value);
                            handleChange(inputObj.uid, "percentReduction", value);
                        }}
                        placeholder="Enter the percentage of load reduction"
                    />
                </div>
            ))}
            <div className="filter-container">
                <Button type="primary" onClick={addConsumptionReduction}>
                    + Add consumption reduction
                </Button>
            </div>
        </div>
    );
}

export default ConsumptionReductionEffectInput;
